import {ChangeEvent, FC, useMemo, useRef, useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Slider,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    CloudUpload,
    Delete,
    ExpandMore,
    Info as InfoIcon,
    PlayArrow,
} from '@mui/icons-material';
import {
    AiImageGroup,
    AiImageMode,
    AiImageProvider,
    AspectRatio,
    CreateAiImageRequestBody,
} from '../../types/aiImage';
import {
    useCreateAiImageRequest,
    useUploadAiImageBaseImage,
} from '../../hooks/useAiImages';

interface Props {
    group: AiImageGroup;
}

const ASPECT_RATIOS: AspectRatio[] = ['1:1', '3:4', '4:3', '9:16', '16:9'];
const OPENAI_APPROX_RATIOS: AspectRatio[] = ['3:4', '4:3', '9:16', '16:9'];
const MAX_BASE_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB

export const AiImageRequestForm: FC<Props> = ({group}) => {
    const [userContext, setUserContext] = useState('');
    const [provider, setProvider] = useState<AiImageProvider>('GEMINI');
    const [mode, setMode] = useState<AiImageMode>('SYNC');
    const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1');
    const [imageCount, setImageCount] = useState(1);
    const [baseImageUrl, setBaseImageUrl] = useState<string | undefined>();
    const [baseImageError, setBaseImageError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const createMut = useCreateAiImageRequest(group.id);
    const uploadMut = useUploadAiImageBaseImage();

    const finalPrompt = useMemo(
        () => `${group.systemContext}\n\n${userContext}`,
        [group.systemContext, userContext]
    );

    // BATCH mode는 image_count=1만 허용 (v1 제약)
    const effectiveImageCount = mode === 'BATCH' ? 1 : imageCount;

    // BATCH + base image 조합 차단 (서버 사전 차단과 동일 정책)
    const isBatchBaseImageConflict = mode === 'BATCH' && !!baseImageUrl;

    // OpenAI + base image 조합 안내 (Step 3.2 TODO)
    const isOpenAiBaseImagePending = provider === 'OPENAI' && !!baseImageUrl;

    // OpenAI + 비율 근사 매핑 안내
    const showOpenAiRatioHint =
        provider === 'OPENAI' && OPENAI_APPROX_RATIOS.includes(aspectRatio);

    const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBaseImageError(null);

        if (!file.type.startsWith('image/')) {
            setBaseImageError('이미지 파일만 업로드 가능합니다.');
            return;
        }
        if (file.size > MAX_BASE_IMAGE_BYTES) {
            setBaseImageError('파일 크기는 10MB 이하여야 합니다.');
            return;
        }
        try {
            const res = await uploadMut.mutateAsync({groupId: group.id, file});
            setBaseImageUrl(res.imageUrl);
        } catch (err) {
            setBaseImageError(err instanceof Error ? err.message : '업로드 실패');
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemoveBaseImage = () => {
        setBaseImageUrl(undefined);
        setBaseImageError(null);
    };

    const canSubmit =
        !!userContext.trim() &&
        !createMut.isPending &&
        !uploadMut.isPending &&
        !isBatchBaseImageConflict;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitError(null);
        const body: CreateAiImageRequestBody = {
            userContext: userContext.trim(),
            provider,
            mode,
            imageCount: effectiveImageCount,
            aspectRatio,
            baseImageUrl,
        };
        try {
            await createMut.mutateAsync(body);
            // 성공 시 폼 일부 리셋 (그룹은 유지, userContext 비우기)
            setUserContext('');
            setBaseImageUrl(undefined);
        } catch (e) {
            setSubmitError(e instanceof Error ? e.message : '요청 실패');
        }
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    이미지 생성 요청
                </Typography>

                <Stack spacing={2}>
                    <TextField
                        label="유저 컨텍스트 (프롬프트에 추가할 내용)"
                        value={userContext}
                        onChange={(e) => setUserContext(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="예: 푸른 하늘 아래 펼쳐진 해바라기 밭, 따뜻한 오후 햇살"
                    />

                    <Accordion variant="outlined">
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" fontWeight={600}>
                                최종 프롬프트 미리보기
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: 'grey.50',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontFamily: 'ui-monospace, monospace',
                                        fontSize: '0.78rem',
                                    }}
                                >
                                    {finalPrompt}
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                        <Box sx={{flex: 1}}>
                            <Typography variant="caption" color="text.secondary">
                                Provider
                            </Typography>
                            <ToggleButtonGroup
                                value={provider}
                                exclusive
                                size="small"
                                fullWidth
                                onChange={(_, v) => v && setProvider(v)}
                            >
                                <ToggleButton value="GEMINI">GEMINI</ToggleButton>
                                <ToggleButton value="OPENAI">OPENAI</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <Box sx={{flex: 1}}>
                            <Typography variant="caption" color="text.secondary">
                                Mode
                            </Typography>
                            <ToggleButtonGroup
                                value={mode}
                                exclusive
                                size="small"
                                fullWidth
                                onChange={(_, v) => v && setMode(v)}
                            >
                                <ToggleButton value="SYNC">SYNC</ToggleButton>
                                <ToggleButton value="BATCH">BATCH</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Stack>

                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                                Aspect Ratio
                            </Typography>
                            {showOpenAiRatioHint && (
                                <Tooltip
                                    title="OpenAI는 정확한 비율 출력을 지원하지 않으므로 가장 가까운 size(2:3 또는 3:2 계열)로 근사 매핑됩니다."
                                    arrow
                                >
                                    <InfoIcon sx={{fontSize: 14, color: 'info.main'}} />
                                </Tooltip>
                            )}
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', mt: 0.5}}>
                            {ASPECT_RATIOS.map((r) => (
                                <Chip
                                    key={r}
                                    label={r}
                                    color={aspectRatio === r ? 'primary' : 'default'}
                                    variant={aspectRatio === r ? 'filled' : 'outlined'}
                                    onClick={() => setAspectRatio(r)}
                                    size="small"
                                />
                            ))}
                        </Stack>
                        {showOpenAiRatioHint && (
                            <Typography
                                variant="caption"
                                color="info.main"
                                sx={{display: 'block', mt: 0.5}}
                            >
                                (OpenAI 근사 매핑: 실제 출력 size는 2:3 또는 3:2에 해당)
                            </Typography>
                        )}
                    </Box>

                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                                Image Count
                            </Typography>
                            <Chip
                                size="small"
                                label={effectiveImageCount}
                                color="primary"
                                variant="outlined"
                            />
                            {mode === 'BATCH' && (
                                <Typography variant="caption" color="text.secondary">
                                    (BATCH 모드는 1장 고정)
                                </Typography>
                            )}
                        </Stack>
                        <Slider
                            value={imageCount}
                            min={1}
                            max={5}
                            step={1}
                            marks
                            disabled={mode === 'BATCH'}
                            onChange={(_, v) => setImageCount(Array.isArray(v) ? v[0] : v)}
                            sx={{mt: 0.5}}
                        />
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.5}}>
                            Base Image (선택, img2img 입력)
                        </Typography>
                        {baseImageUrl ? (
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={baseImageUrl}
                                    alt="base"
                                    sx={{
                                        maxHeight: 160,
                                        maxWidth: 240,
                                        display: 'block',
                                        bgcolor: 'grey.100',
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={handleRemoveBaseImage}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        bgcolor: 'background.paper',
                                        '&:hover': {bgcolor: 'error.light', color: 'error.contrastText'},
                                    }}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        ) : (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={
                                    uploadMut.isPending ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <CloudUpload />
                                    )
                                }
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadMut.isPending}
                            >
                                {uploadMut.isPending ? '업로드 중...' : '이미지 선택'}
                            </Button>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            style={{display: 'none'}}
                        />
                        {baseImageError && (
                            <Alert severity="error" sx={{mt: 1}}>
                                {baseImageError}
                            </Alert>
                        )}
                    </Box>

                    {/* BATCH + base image 조합 차단 안내 */}
                    {isBatchBaseImageConflict && (
                        <Alert severity="warning">
                            BATCH 모드는 base image와 함께 사용할 수 없습니다. SYNC 모드로
                            전환하거나 base image를 제거해 주세요.
                        </Alert>
                    )}

                    {/* OpenAI base image 준비 중 안내 */}
                    {isOpenAiBaseImagePending && (
                        <Alert severity="info">
                            OpenAI base image(img2img) 기능은 현재 준비 중입니다. 요청은
                            전송되지만 실패할 수 있으니 GEMINI 사용을 권장합니다.
                        </Alert>
                    )}

                    {submitError && <Alert severity="error">{submitError}</Alert>}

                    <Stack direction="row" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            startIcon={<PlayArrow />}
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                        >
                            {createMut.isPending ? '요청 중...' : '생성하기'}
                        </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        SYNC: 즉시 생성 (~5-15초) / BATCH: 비동기 큐 (~1-6시간, 1장 고정)
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
};
